/**
 * The Stocktake Progress filter — two numeric "greater than" thresholds.
 *
 * Shared by the Progress tab (filtering location rows) and the location detail
 * screen (filtering SKU rows), because a filter applied on Progress travels to
 * the detail screen on the URL and has to mean the same thing in both places.
 *
 * A blank or non-numeric input is `null`, i.e. that axis is inactive. That is
 * the only "off" representation, so `activeFilterCount` can just count the
 * non-null axes.
 */

export interface StocktakeFilter {
  readonly valueGreaterThan: number | null
  readonly qtyGreaterThan: number | null
}

export type StocktakeFilterKey = 'qty' | 'value'

export const EMPTY_STOCKTAKE_FILTER: StocktakeFilter = {
  valueGreaterThan: null,
  qtyGreaterThan: null,
}

/** One populated input is one filter — this is what "· 2 filters" counts. */
export function activeFilterCount(filter: StocktakeFilter): number {
  let count = 0
  if (filter.qtyGreaterThan !== null) count += 1
  if (filter.valueGreaterThan !== null) count += 1
  return count
}

export function hasActiveFilter(filter: StocktakeFilter): boolean {
  return activeFilterCount(filter) > 0
}

/**
 * Text → threshold. Anything that is not a finite number is `null` rather than
 * `NaN`, so a half-typed "1e" never silently filters everything out.
 */
export function parseThreshold(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}

/** `null` → '' so the same value round-trips through a controlled input. */
export function thresholdToInput(value: number | null): string {
  return value === null ? '' : String(value)
}

export interface StocktakeFilterChip {
  readonly key: StocktakeFilterKey
  readonly label: string
}

/** Chip labels as the design writes them: `Qty > 500`, `Value > $1,000`. */
export function filterChips(filter: StocktakeFilter): StocktakeFilterChip[] {
  const chips: StocktakeFilterChip[] = []
  if (filter.qtyGreaterThan !== null) {
    chips.push({ key: 'qty', label: `Qty > ${filter.qtyGreaterThan.toLocaleString('en-US')}` })
  }
  if (filter.valueGreaterThan !== null) {
    chips.push({
      key: 'value',
      label: `Value > $${filter.valueGreaterThan.toLocaleString('en-US')}`,
    })
  }
  return chips
}

/** Drop one axis — what a chip's X does. */
export function withoutChip(filter: StocktakeFilter, key: StocktakeFilterKey): StocktakeFilter {
  return key === 'qty' ? { ...filter, qtyGreaterThan: null } : { ...filter, valueGreaterThan: null }
}

/** Strictly greater than, on both axes, for a row that has a qty and a value. */
export function matchesStocktakeFilter(
  filter: StocktakeFilter,
  row: Readonly<{ qty: number; value: number }>,
): boolean {
  if (filter.qtyGreaterThan !== null && row.qty <= filter.qtyGreaterThan) return false
  if (filter.valueGreaterThan !== null && row.value <= filter.valueGreaterThan) return false
  return true
}

/**
 * `'$3,458.71'` → `3458.71`. The authored mock rows carry money as display
 * strings, and both screens were already parsing them ad hoc.
 */
export function parseMoney(display: string): number {
  return Number.parseFloat(display.replace(/[$,]/g, ''))
}

/** `'1,240'` → `1240`. Same reason as `parseMoney`. */
export function parseQty(display: string): number {
  return Number.parseInt(display.replaceAll(',', ''), 10)
}

/** Round-trip the filter through the URL so it survives navigation. */
export function filterToSearchParams(filter: StocktakeFilter): Record<string, string> {
  const out: Record<string, string> = {}
  if (filter.qtyGreaterThan !== null) out.qtyGt = String(filter.qtyGreaterThan)
  if (filter.valueGreaterThan !== null) out.valueGt = String(filter.valueGreaterThan)
  return out
}

export function filterFromSearchParams(params: URLSearchParams): StocktakeFilter {
  return {
    qtyGreaterThan: parseThreshold(params.get('qtyGt') ?? ''),
    valueGreaterThan: parseThreshold(params.get('valueGt') ?? ''),
  }
}
